import React, { useEffect, useState } from "react";
import ServiceToggleCard from "./ServiceToggleCard";
import { useMetaSignup } from "../../hooks/useMetaSignup";
import { WhatsAppAccountStatus } from "../WhatsAppAccountStatus";
import { useWhatsAppAccountStatus } from "../../hooks/useWhatsAppAccountStatus";
import {
  completeWhatsAppOnboarding,
  createWhatsAppOnboardingSession,
  fetchWhatsAppOnboardingReadiness,
} from "../../api/whatsappApi";

const KNOWN_SERVICES = [
  { service_key: "customer_support", status: "setup_required", config_json: {} },
  { service_key: "document_rag", status: "setup_required", config_json: {} },
  { service_key: "voice_support", status: "setup_required", config_json: {} },
  // { service_key: "appointment_booking", status: "setup_required", config_json: {} },
];

const getReadinessErrorMessage = (error) => {
  if (error?.status === 403) return "Access denied.";
  if (error?.status === 404) return "Tenant not found.";
  if (error?.status === 500) return "Something went wrong. Please retry.";
  return error?.message || "Unable to check WhatsApp onboarding readiness.";
};

const getSessionErrorCode = (error) =>
  error?.details?.code || 
  error?.details?.error_code || 
  error?.details?.error?.code || 
  error?.details?.data?.error?.code || 
  error?.details?.data?.code;

const getSessionErrorMessage = (error) => {
  if (error?.status === 409 && getSessionErrorCode(error) === "WHATSAPP_ACTIVE_ONBOARDING_SESSION_EXISTS") {
    return "A WhatsApp onboarding session is already active for this tenant.";
  }
  if (error?.status === 410) return "WhatsApp setup session timed out. Start again or try again.";
  if (error?.status === 422) return "WhatsApp setup session could not be verified. Start again or try again.";
  if (error?.status === 403) return "Access denied.";
  if (error?.status === 404) return "Tenant not found.";
  if (error?.status === 502) return "Meta authorization could not be completed. Please try again.";
  if (error?.status === 503) return "Meta is temporarily unavailable. Please try again.";
  if (error?.status === 500) return "Something went wrong while starting WhatsApp setup. Please retry.";
  return error?.message || "Unable to start WhatsApp onboarding.";
};

const normalizeSessionData = (response) =>
  response?.data || response?.active_session || response?.existing_session || response?.session || response || null;

const normalizeCompletionData = (response) => response?.data?.data || response?.data || response || null;



const TenantServicesTab = ({ tenantId, services, onToggleService, disabled }) => {
  const [whatsAppReadiness, setWhatsAppReadiness] = useState(null);
  const [whatsAppReadinessError, setWhatsAppReadinessError] = useState("");
  const [checkingWhatsAppReadiness, setCheckingWhatsAppReadiness] = useState(false);
  const [whatsAppOnboardingSession, setWhatsAppOnboardingSession] = useState(null);
  const [whatsAppSignupResult, setWhatsAppSignupResult] = useState(null);
  const [whatsAppAccountProgress, setWhatsAppAccountProgress] = useState(null);
  
  const { accountStatus, loading: accountLoading, error: accountError, refetch: refetchAccountStatus } = useWhatsAppAccountStatus(tenantId);

  const servicesArray = Array.isArray(services) ? services : (services?.data || []);
  const isCustomerSupportEnabledViaAccount = accountStatus?.status === "connected";
  
  const hasConfiguredServices = 
    servicesArray.some(s => s.status && s.status !== "setup_required") || 
    isCustomerSupportEnabledViaAccount;
    
  const displayServices = KNOWN_SERVICES.map(known => {
    const existing = servicesArray.find(s => s.service_key === known.service_key);
    return existing || known;
  });
  const { openSignup } = useMetaSignup(
    (result) => {
      console.log("[MetaSignup] service toggle result", {
        event: result.event,
        hasWabaId: !!result.data?.waba_id,
        hasPhoneNumberId: !!result.data?.phone_number_id,
        hasBusinessId: !!result.data?.business_id,
      });
      setWhatsAppSignupResult(result);
      if (result.event === "session_expired") {
        setWhatsAppReadinessError("WhatsApp setup session timed out. Start again or try again.");
        setWhatsAppOnboardingSession(null);
      }
    },
    async (payload) => {
      try {
        const response = await completeWhatsAppOnboarding(tenantId, payload);
        const progress = normalizeCompletionData(response);

        console.log("[WhatsAppCompleteOnboarding] progress state", {
          accountId: progress?.account_id,
          status: progress?.status,
          stage: progress?.stage,
          hasStatusEndpoint: !!progress?.status_endpoint,
        });
        setWhatsAppAccountProgress(progress);
        // Refresh the account status card now that onboarding has completed.
        refetchAccountStatus();
      } catch (error) {
        setWhatsAppReadinessError(getSessionErrorMessage(error));
        throw error;
      }
    }
  );

  useEffect(() => {
    if (!whatsAppOnboardingSession?.expires_at) return undefined;

    const expiresAt = new Date(whatsAppOnboardingSession.expires_at).getTime();
    const delay = expiresAt - Date.now();

    if (!Number.isFinite(expiresAt) || delay <= 0) {
      setWhatsAppReadinessError("WhatsApp setup session expired. Start setup again from this screen.");
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setWhatsAppReadinessError("WhatsApp setup session expired. Start setup again from this screen.");
      setWhatsAppOnboardingSession(null);
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [whatsAppOnboardingSession]);

  const launchWhatsAppSignup = async (sessionData) => {
    if (!sessionData?.launch) {
      setWhatsAppReadinessError("WhatsApp setup session started, but launch details were missing.");
      return;
    }

    console.log("[WhatsAppOnboardingSession] opening Meta signup", {
      onboardingSessionId: sessionData.onboarding_session_id,
      expiresAt: sessionData.expires_at,
      hasSessionToken: !!sessionData.session_token,
      hasLaunch: !!sessionData.launch,
    });
    await openSignup(sessionData.launch, {
      onboardingSessionId: sessionData.onboarding_session_id,
      sessionToken: sessionData.session_token,
      expiresAt: sessionData.expires_at,
    });
  };

  const startWhatsAppSession = async ({ restartExisting = false } = {}) => {
    const response = await createWhatsAppOnboardingSession(tenantId, { restartExisting });
    const sessionData = normalizeSessionData(response);

    setWhatsAppOnboardingSession({
      onboarding_session_id: sessionData?.onboarding_session_id,
      session_token: sessionData?.session_token,
      expires_at: sessionData?.expires_at,
      launch: sessionData?.launch,
    });
    setWhatsAppSignupResult(null);
    setWhatsAppAccountProgress(null);
    await launchWhatsAppSignup(sessionData);
  };

  const handleConnectWhatsApp = async () => {
    if (!tenantId || checkingWhatsAppReadiness) return;

    console.log("[WhatsAppReadiness] toggle clicked", { tenantId });
    setCheckingWhatsAppReadiness(true);
    setWhatsAppReadiness(null);
    setWhatsAppReadinessError("");
    setWhatsAppOnboardingSession(null);
    setWhatsAppSignupResult(null);
    setWhatsAppAccountProgress(null);
    let sessionAttempted = false;

    try {
      const response = await fetchWhatsAppOnboardingReadiness(tenantId);
      const data = response?.data || {};
      setWhatsAppReadiness(data);
      console.log("[WhatsAppReadiness] validation result", {
        ready: data.ready,
        missingFields: data.validation?.missing_fields || [],
        invalidFields: data.validation?.invalid_fields || [],
        actions: data.actions,
      });

      if (!data.ready) {
        console.warn("[WhatsAppReadiness] signup blocked by validation", {
          missingFields: data.validation?.missing_fields || [],
          invalidFields: data.validation?.invalid_fields || [],
        });
        return;
      }
      if (!data.actions?.can_start_signup) {
        console.warn("[WhatsAppReadiness] signup blocked by actions", {
          actions: data.actions,
        });
        setWhatsAppReadinessError("WhatsApp setup cannot be started for this tenant yet.");
        return;
      }

      sessionAttempted = true;
      await startWhatsAppSession({ restartExisting: false });
    } catch (error) {
      console.log("[WhatsAppReadiness] catch block entered", {
        status: error?.status,
        code: getSessionErrorCode(error),
        details: error?.details,
      });

      if (error?.details?.validation || error?.details?.data?.validation) {
        console.log("[WhatsAppReadiness] readiness validation failed");
        setWhatsAppReadiness(error?.details?.data || error?.details);
        setWhatsAppReadinessError(getReadinessErrorMessage(error));
      } else {
        console.log("[WhatsAppReadiness] unhandled error");
        setWhatsAppReadinessError(sessionAttempted ? getSessionErrorMessage(error) : getReadinessErrorMessage(error));
      }
    } finally {
      setCheckingWhatsAppReadiness(false);
    }
  };

  const handleRestartWhatsAppSession = async () => {
    if (!tenantId || checkingWhatsAppReadiness) return;
    setCheckingWhatsAppReadiness(true);
    setWhatsAppReadinessError("");

    try {
      await startWhatsAppSession({ restartExisting: true });
    } catch (error) {
      setWhatsAppReadinessError(getSessionErrorMessage(error));
    } finally {
      setCheckingWhatsAppReadiness(false);
    }
  };



  const isCustomerSupportEnabled = 
    displayServices.find((s) => s.service_key === "customer_support")?.status === "enabled" || 
    isCustomerSupportEnabledViaAccount;

  return (
    <div className="space-y-4">
      {/* WhatsApp account live status — always visible for this tenant */}
      <WhatsAppAccountStatus
        tenantId={tenantId}
        onStartSignup={handleConnectWhatsApp}
        onDisconnect={() => {}}
        onRetry={() => {}}
        accountStatus={accountStatus}
        loading={accountLoading}
        error={accountError}
        refetch={refetchAccountStatus}
        isCustomerSupportEnabled={isCustomerSupportEnabled}
      />
      {!hasConfiguredServices ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No services are configured yet. Enable a service below to create it.
        </div>
      ) : null}
      <div className="grid gap-4 xl:grid-cols-2">
        {displayServices.map((service) => (
          <ServiceToggleCard
            key={service.service_key}
            service={service}
            onToggle={onToggleService}
            onConnectWhatsApp={handleConnectWhatsApp}
            onForceRestartWhatsApp={handleRestartWhatsAppSession}
            whatsAppReadiness={whatsAppReadiness}
            whatsAppReadinessError={whatsAppReadinessError}
            whatsAppOnboardingSession={whatsAppOnboardingSession}
            whatsAppSignupResult={whatsAppSignupResult}
            whatsAppAccountProgress={whatsAppAccountProgress}
            checkingWhatsAppReadiness={checkingWhatsAppReadiness}
            disabled={disabled}
            accountStatus={accountStatus}
          />
        ))}
      </div>

    </div>
  );
};

export default TenantServicesTab;
